// RebuildPositionsFromActivities.js
//
// ✅ Source of truth = BuildActivity output
//    - open_qty, pl, pl_delta, tax are already correct
// ✅ No P&L recalculation from prices
// ✅ No resorting inside each group
//    (we trust the order that BuildActivity produced)
//
// Semantics (same as ActivitySummary):
// - For a trade group:
//   • last row's open_qty === 0  → position is CLOSED
//   • last row's open_qty !== 0  → position is still OPEN
// - GROUP P&L = last row's pl_delta (for closed groups), else 0.
// - Day total P&L = sum(pl_delta) across all groups = last row's pl for the day. :contentReference[oaicite:0]{index=0}

/** Safe number parse */
const toNumber = (v) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : 0;
  };
  
  /** Prefer submitted_at_gmt, fall back to created_at_gmt */
  const pickTimestamp = (activity) => {
    if (activity.submitted_at_gmt) return activity.submitted_at_gmt;
    if (activity.created_at_gmt) return activity.created_at_gmt;
    return null;
  };
  
  /** Hold time in minutes between two ISO timestamps */
  const computeHoldMinutes = (entryTs, exitTs) => {
    if (!entryTs || !exitTs) return 0;
    try {
      const entry = new Date(entryTs);
      const exit = new Date(exitTs);
      if (Number.isNaN(entry.getTime()) || Number.isNaN(exit.getTime())) return 0;
      return Math.round((exit - entry) / 60000);
    } catch {
      return 0;
    }
  };
  
  /** Rough exit type label based only on last activity */
  const determineExitType = (firstActivity, lastActivity, isClosed) => {
    if (!isClosed || !lastActivity) return "Open";
  
    const t = (lastActivity.type || "").toLowerCase();
    const lastSide = (lastActivity.side || "").toLowerCase();
    const firstSide = (firstActivity.side || "").toLowerCase();
  
    if (t === "stop" || t === "stop_limit") return "Stop Loss";
    if (t === "limit" && lastSide !== firstSide) return "Target Fill";
    if (t === "market") return "Market Close";
  
    // Fallbacks based on side reversal
    if (lastSide === "sell" && firstSide === "buy") return "Target Fill";
    if (lastSide === "buy" && firstSide === "sell_short") return "Cover";
  
    return "Market Close";
  };
  
  /** Simple R:R ratio from entry / target / stop (no P&L math) */
  const calculateRRRatio = (entry, target, stop) => {
    const e = toNumber(entry);
    const t = toNumber(target);
    const s = toNumber(stop);
    if (!e || !t || !s) return 0;
  
    const reward = Math.abs(t - e);
    const risk = Math.abs(e - s);
    if (!risk) return 0;
  
    return parseFloat((reward / risk).toFixed(2));
  };
  
  /**
   * Build a single position object from ALL activities in a group
   * WITHOUT changing their order.
   */
  const buildPositionFromGroup = (groupId, groupActivities) => {
    if (!groupActivities || groupActivities.length === 0) return null;
  
    // IMPORTANT: do NOT sort here.
    // BuildActivity already encoded the correct sequence;
    // open_qty & pl_delta semantics depend on that order. :contentReference[oaicite:1]{index=1}
    const first = groupActivities[0];
    const last = groupActivities[groupActivities.length - 1];
  
    const openQty = toNumber(last.open_qty);
    const isClosed = openQty === 0;
    const symbol = first.symbol || "SPY";
    const rawSide = (first.side || "").toLowerCase();
    const isLong = rawSide === "buy";
  
    // -----------------------------
    // Entry / Exit price aggregation
    // -----------------------------
    const entryLegs = groupActivities.filter((a) => {
      const s = (a.side || "").toLowerCase();
      return s === "buy" || s === "sell_short";
    });
  
    let totalEntryQty = 0;
    let totalEntryValue = 0;
    entryLegs.forEach((a) => {
      const qty = Math.abs(toNumber(a.qty));
      const price = toNumber(a.price);
      totalEntryQty += qty;
      totalEntryValue += qty * price;
    });
  
    const entryPrice =
      totalEntryQty > 0 ? totalEntryValue / totalEntryQty : toNumber(first.price);
  
    let exitPrice = 0;
    if (isClosed) {
      const exitLegs = groupActivities.filter((a) => {
        const s = (a.side || "").toLowerCase();
        return isLong ? s === "sell" : s === "buy";
      });
  
      let totalExitQty = 0;
      let totalExitValue = 0;
      exitLegs.forEach((a) => {
        const qty = Math.abs(toNumber(a.qty));
        const price = toNumber(a.price);
        totalExitQty += qty;
        totalExitValue += qty * price;
      });
  
      exitPrice =
        totalExitQty > 0 ? totalExitValue / totalExitQty : toNumber(last.price);
    }
  
    // -----------------------------
    // P&L – FOLLOW BuildActivity / ActivitySummary
    // -----------------------------
    // - For CLOSED groups: groupPnl = last.pl_delta (non-zero on the close row)
    // - For OPEN groups: groupPnl = 0 (no pl_delta yet)
    const groupPnl = isClosed ? toNumber(last.pl_delta) : 0;
  
    // Day-level running total P&L after this group (if you need it)
    const cumulativePl = toNumber(last.pl);
  
    // -----------------------------
    // Fees / taxes – sum tax across all legs
    // -----------------------------
    const totalTax = groupActivities.reduce(
      (sum, a) => sum + toNumber(a.tax),
      0
    );
  
    // -----------------------------
    // Times & hold
    // -----------------------------
    const entryTime = first.created_at_gmt || first.submitted_at_gmt || null;
    const exitTime = isClosed
      ? last.submitted_at_gmt || last.created_at_gmt || null
      : null;
    const holdTimeMin = computeHoldMinutes(entryTime, exitTime || entryTime);
  
    // -----------------------------
    // Target / stop / RR
    // (we just read them if present; no P&L math)
    // -----------------------------
    const target = toNumber(first.target);
    const stop = toNumber(first.stop);
    const rrRatio = calculateRRRatio(entryPrice, target, stop);
  
    // Exit type label
    const exitType = determineExitType(first, last, isClosed);
  
    // Build final position row
    return {
      // Identity
      id: groupId,
      a_id: first.a_id || first.id || groupId,
      group: groupId,
      symbol,
      side: first.side,
      isLong,
  
      // Sizing
      qty: totalEntryQty,
      open_qty: openQty,
  
      // Prices / times
      entryPrice,
      exitPrice,
      entryTime,
      exitTime,
      holdTimeMin,
  
      // P&L (group level)
      // NOTE: field name kept as "unrealizedPL" for compatibility with
      // TradingCommand / DayPnLSummary, even though for CLOSED positions
      // this is actually realized group P&L.
      unrealizedPL: groupPnl,
      cumulativePl, // optional: day running P&L after this group
  
      // Risk / brackets
      target,
      stop,
      rrRatio,
  
      // Fees
      tax: totalTax,
  
      // Simple win flag
      isWin: groupPnl > 0,
  
      // Exit classification
      exitType,
  
      // Full activity trail (easy drill-through in modals)
      activities: groupActivities,
    };
  };
  
  /**
   * Main: rebuild positions from BuildActivity output.
   * Returns active + closed arrays (based ONLY on last.open_qty).
   */
  export const rebuildPositionsFromActivities = (activities) => {
    if (!Array.isArray(activities) || activities.length === 0) {
      return { allPositions: [], activePositions: [], closedPositions: [] };
    }
  
    // Group by `group` while PRESERVING ORIGINAL ORDER
    const groups = new Map();
  
    activities.forEach((a) => {
      const rawGroup = a.group ?? 0;
      const groupId = Number(rawGroup) || 0;
      if (!groups.has(groupId)) {
        groups.set(groupId, []);
      }
      groups.get(groupId).push(a);
    });
  
    const allPositions = [];
    const activePositions = [];
    const closedPositions = [];
  
    for (const [groupId, groupActivities] of groups.entries()) {
      const pos = buildPositionFromGroup(groupId, groupActivities);
      if (!pos) continue;
  
      allPositions.push(pos);
  
      if ((pos.open_qty ?? 0) === 0) {
        closedPositions.push(pos);
      } else {
        activePositions.push(pos);
      }
    }
  
    // Sort by group id just for display consistency
    allPositions.sort((a, b) => a.group - b.group);
    activePositions.sort((a, b) => a.group - b.group);
    closedPositions.sort((a, b) => a.group - b.group);
  
    return { allPositions, activePositions, closedPositions };
  };
  
  /**
   * Sum group-level P&L (same as summing pl_delta by group).
   * Use this on either closedPositions or allPositions.
   */
  export const calculateTotalPnL = (positions = []) =>
    positions.reduce(
      (sum, p) => sum + (toNumber(p.unrealizedPL) || 0),
      0
    );
  
  /** Convenience helpers */
  export const calculateClosedPnL = (closedPositions = []) =>
    calculateTotalPnL(closedPositions);
  
  export const calculateActivePnL = (activePositions = []) =>
    calculateTotalPnL(activePositions);
  
  /**
   * Optional sanity check:
   * - sum of group P&L across ALL groups should equal
   *   the last activity's pl (day total) from BuildActivity.
   */
  export const validatePositionsAgainstActivities = (activities, allPositions) => {
    if (!Array.isArray(activities) || activities.length === 0) {
      return { isValid: true, errors: [], warnings: [] };
    }
  
    const errors = [];
    const warnings = [];
  
    const dayTotalFromActivities = toNumber(activities[activities.length - 1].pl);
    const totalFromPositions = calculateTotalPnL(allPositions);
  
    if (Math.abs(dayTotalFromActivities - totalFromPositions) > 0.01) {
      errors.push(
        `P&L mismatch: activities total=${dayTotalFromActivities.toFixed(
          2
        )} vs positions total=${totalFromPositions.toFixed(2)}`
      );
    }
  
    return {
      isValid: errors.length === 0,
      errors,
      warnings,
    };
  };
  
  export default {
    rebuildPositionsFromActivities,
    calculateTotalPnL,
    calculateClosedPnL,
    calculateActivePnL,
    validatePositionsAgainstActivities,
  };
  